import {SuccessList} from "@portal/api/responses/api-response";
import {map} from "rxjs";
import { Injectable } from '@angular/core';
import { ApiBaseService } from '@portal/api/api-base-service';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse, ListResponse } from '@portal/api/responses/api-response';
import { SearchRequest } from '@portal/api/requests/search-request';
import { Guid } from 'guid-typescript';
import { SponsorAddRequest } from '@portal/api/requests/sponsors/sponsor-add-request';
import { SponsorUpdateRequest } from '@portal/api/requests/sponsors/sponsor-update-request';
import { SponsorListResponse } from '@portal/api/responses/sponsors/sponsor-list-response';
import { SponsorsSummaryResponse } from '@portal/api/responses/sponsors/sponsors-summary-response';
import {DonationResponse} from "@portal/api/responses/donations/donation-response";
import {CollaboratorResponse} from "@portal/api/responses/collaborators/collaborator-response";
import {DonationReportRequest} from "@portal/api/requests/donations/donation-report-request";
import {SponsorsReportRequest} from "@portal/api/requests/sponsors/sponsors-report-request";
import {StorageService} from "@portal/shared/services/storage.service";
import {PartySelectedService} from "@portal/shared/services/party-selected.service";
import {OrganizationType} from "@portal/api/enums/organization-type";

@Injectable({
    providedIn: 'root'
})

export class SponsorsService extends ApiBaseService {
    constructor(
        public http: HttpClient,
        private _storageService: StorageService,
        private _partySelectedService: PartySelectedService) {
        super(http);
    }


    add(obj: SponsorAddRequest): Observable<ApiResponse<SponsorListResponse>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        const payload: SponsorAddRequest & { partyId?: Guid } = {...obj, partyId: (obj as any).partyId ?? partyId};
        if (this.isOrgScope() || !partyId) {
            return this.post<ApiResponse<SponsorListResponse>, SponsorAddRequest & { partyId?: Guid }>(payload, `v1/orgs/${orgId}/sponsors/add`);
        }
        return this.post<ApiResponse<SponsorListResponse>, SponsorAddRequest & { partyId?: Guid }>(payload, `v1/orgs/${orgId}/parties/${partyId}/sponsors/add`);
    }

    update(obj: SponsorUpdateRequest): Observable<ApiResponse<SponsorListResponse>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.put<ApiResponse<SponsorListResponse>, SponsorUpdateRequest>(obj, `v1/orgs/${orgId}/sponsors/update`);
        }
        return this.put<ApiResponse<SponsorListResponse>, SponsorUpdateRequest>(obj, `v1/orgs/${orgId}/parties/${partyId}/sponsors/update`);
    }

    getFormEdit(sponsorId: string): Observable<ApiResponse<SponsorUpdateRequest>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.get<ApiResponse<SponsorUpdateRequest>>(`v1/orgs/${orgId}/sponsors/form/edit/${sponsorId}`);
        }
        return this.get<ApiResponse<SponsorUpdateRequest>>(`v1/orgs/${orgId}/parties/${partyId}/sponsors/form/edit/${sponsorId}`);
    }

    listByParty(obj: SearchRequest, activityId: Guid = null): Observable<ApiResponse<ListResponse<SponsorListResponse[]>>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            if (activityId != null) {
                return this.post<ApiResponse<ListResponse<SponsorListResponse[]>>, SearchRequest>(obj, `v1/orgs/${orgId}/sponsors/listallbyactivity?activityId=${activityId}`);
            }
            return this.post<ApiResponse<ListResponse<SponsorListResponse[]>>, SearchRequest>(obj, `v1/orgs/${orgId}/sponsors/listall`);
        }
        if (activityId == null) {
            return this.post<ApiResponse<ListResponse<SponsorListResponse[]>>, SearchRequest>(obj, `v1/orgs/${orgId}/parties/${partyId}/sponsors/listallbyparty`);
        }
        return this.post<ApiResponse<ListResponse<SponsorListResponse[]>>, SearchRequest>(obj, `v1/orgs/${orgId}/parties/${partyId}/sponsors/listallbyactivity?activityId=${activityId}`);
    }

    deleteSponsor(id: Guid): Observable<ApiResponse<boolean>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.deleteBase<ApiResponse<boolean>>(`v1/orgs/${orgId}/sponsors/${id}`);
        }
        return this.deleteBase<ApiResponse<boolean>>(`v1/orgs/${orgId}/parties/${partyId}/sponsors/${id}`);
    }

    summary(partyId: Guid): Observable<ApiResponse<SponsorsSummaryResponse>> {
        const orgId = this.getOrgId();
        const selectedPartyId = this.getPartyId() || partyId;
        return this.get<ApiResponse<SponsorsSummaryResponse>>(`v1/orgs/${orgId}/parties/${selectedPartyId}/sponsors/summary`);
    }

    getCollaborators() : Observable<CollaboratorResponse[]> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.get<SuccessList<CollaboratorResponse>>(`v1/orgs/${orgId}/collaborators/dropdown`).pipe(map((response) => response.data));
        }
        return this.get<SuccessList<CollaboratorResponse>>(`v1/orgs/${orgId}/parties/${partyId}/collaborators/dropdown`).pipe(map((response) => response.data));
    }

    report(resultRequest : SponsorsReportRequest): Observable<HttpResponse<Blob>> {
        const orgId = this.getOrgId();
        const partyId = this.getPartyId();
        if (this.isOrgScope() || !partyId) {
            return this.postFile(`v1/orgs/${orgId}/sponsors/report`, resultRequest);
        }
        return this.postFile(`v1/orgs/${orgId}/parties/${partyId}/sponsors/report`, resultRequest);
    }

    private getOrgId(): Guid {
        return this._storageService.getOrg()?.id;
    }

    private getPartyId(): Guid {
        return this._partySelectedService.getPartyId() ?? this._storageService.getParty()?.id;
    }

    private isOrgScope(): boolean {
        return this._storageService.getOrg()?.type === OrganizationType.Association;
    }
}
